import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { checkoutLocal, normWa } from "./store";
import { rp } from "./util";

const field = "h-11 w-full rounded border border-mist bg-white px-3 text-base outline-none focus:border-sage-dark";

export default function CheckoutDialog({ open, onClose, event, cart, byId, customers, onDone }) {
    const [wa, setWa] = useState("");
    const [name, setName] = useState("");
    const [method, setMethod] = useState("");
    const [busy, setBusy] = useState(false);
    const methods = event?.payment_methods?.length ? event.payment_methods : ["Cash"];
    const nwa = normWa(wa);
    const known = useMemo(() => customers.find((c) => c.wa === nwa), [customers, nwa]);
    const total = cart.reduce((s, l) => s + l.qty * (byId.get(l.item_id)?.price || 0), 0);

    useEffect(() => { if (open) { setWa(""); setName(""); setMethod(""); } }, [open]);
    useEffect(() => { if (known && !name) setName(known.name); }, [known]); // eslint-disable-line react-hooks/exhaustive-deps

    const complete = async () => {
        if (nwa.length < 8) return toast.error("Enter a valid WhatsApp number");
        if (!name.trim()) return toast.error("Enter the customer name");
        if (!method) return toast.error("Choose a payment method");
        setBusy(true);
        try {
            const txn = await checkoutLocal(event, cart, { name, wa: nwa, method });
            toast.success(`Sale saved · ${txn.number}`);
            await onDone(txn);
        } catch (e) {
            toast.error(e.message || "Could not save sale");
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="max-w-md" data-testid="pos-checkout-dialog">
                <DialogHeader><DialogTitle className="font-serif text-xl">Checkout</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <label className="block text-[10px] uppercase tracking-widest text-neutral-500">WhatsApp Number
                        <input className={field} inputMode="tel" value={wa} onChange={(e) => setWa(e.target.value)} placeholder="08xx xxxx xxxx" data-testid="pos-checkout-wa" autoFocus />
                    </label>
                    {known && <p className="text-xs text-sage-dark" data-testid="pos-checkout-known">Returning customer · {known.name}</p>}
                    <label className="block text-[10px] uppercase tracking-widest text-neutral-500">Customer Name
                        <input className={field} value={name} onChange={(e) => setName(e.target.value)} data-testid="pos-checkout-name" />
                    </label>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Payment Method</p>
                        <div className="grid grid-cols-3 gap-1.5">
                            {methods.map((m) => (
                                <button key={m} onClick={() => setMethod(m)} data-testid={`pos-pay-${m.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`h-11 rounded border text-sm transition-colors ${method === m ? "border-sage-dark bg-sage-dark text-white" : "border-mist bg-white hover:border-sage-dark"}`}>{m}</button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-baseline justify-between border-t border-mist pt-3">
                        <span className="text-xs uppercase tracking-widest text-neutral-500">Total</span>
                        <span className="text-2xl font-semibold text-ink" data-testid="pos-checkout-total">{rp(total)}</span>
                    </div>
                    <button onClick={complete} disabled={busy} data-testid="pos-complete-sale"
                        className="h-14 w-full rounded-md bg-sage-dark text-white text-sm font-semibold uppercase tracking-[0.2em] hover:opacity-90 disabled:opacity-50">
                        {busy ? "Saving…" : "Complete Sale"}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
